import React, { useState } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import { css } from 'emotion';
import { environment } from './lib/createRelayEnvironment';

const field = css`
    min-width: 24em;
    padding: 0.4em;
    margin-right: 0.5em;
`

/**
 * Resolves whatever identifier we were given and sends us to that user's page.
 *
 * Every user has an intercom id (it defaults to a uuid4), so redirecting into
 * the existing /au/:auIntercomId route always works and nothing downstream
 * needs to know a lookup happened.
 */
function LookupResult({ query }: { query: string }) {
    return (
        <QueryRenderer
            environment={environment}
            query={graphql`
                query AppUserLookupQuery($query: String!) {
                    appuserLookup(query: $query) {
                        intercomExternalUserId
                    }
                }
            `}
            variables={{ query }}
            render={({ error, props }: { error: any, props: any }) => {
                if (error) {
                    return <div>Error: {error.message} 🐞</div>;
                }
                if (!props) {
                    return <div>Searching…</div>;
                }
                if (!props.appuserLookup) {
                    return <div>No user matches “{query}”. 👻</div>;
                }
                return <Redirect to={`/au/${props.appuserLookup.intercomExternalUserId}`} />;
            }}
        />
    );
}

export default function AppUserLookup() {
    const [term, setTerm] = useState('');
    // held separately from `term` so we only query on submit, not per keystroke
    const [submitted, setSubmitted] = useState<string | null>(null);

    return (
        <div>
            <h2>Find a user</h2>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmitted(term.trim() || null);
                }}
            >
                <input
                    className={field}
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="email address / user id / internal or intercom uuid"
                    aria-label="User email address, id, or uuid"
                />
                <button type="submit" disabled={!term.trim()}>Find user</button>
            </form>
            {submitted && <LookupResult query={submitted} />}
        </div>
    );
}
